import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import "./styles/blogsPage.css";

function formatDate(dateValue) {
  if (!dateValue) return "";
  return new Date(dateValue).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function CardSkeleton() {
  return (
    <div className="blog-list-card is-skeleton" aria-hidden="true">
      <div className="blog-list-frame skeleton" />
      <div className="blog-list-body">
        <div className="skeleton skeleton-line skeleton-line--meta" />
        <div className="skeleton skeleton-line skeleton-line--title" />
        <div className="skeleton skeleton-line skeleton-line--title short" />
        <div className="skeleton skeleton-line skeleton-line--excerpt" />
        <div className="skeleton skeleton-line skeleton-line--excerpt short" />
        <div className="skeleton skeleton-line skeleton-line--cta" />
      </div>
    </div>
  );
}

export default function BlogsPage() {
  const [blogs, setBlogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(6);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [filters, setFilters] = useState({
    search: "",
    category: "",
  });

  const [searchInput, setSearchInput] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const fetchBlogs = async (
    pageNumber = 1,
    activeSearch = filters.search,
    activeCategory = filters.category
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const params = {
        status: "PUBLISHED",
        page: pageNumber,
        limit,
      };

      if (activeSearch) params.search = activeSearch;
      if (activeCategory) params.category = activeCategory;

      const res = await api.get("/blogs", { params });

      setBlogs(res.data?.blogs || []);
      setTotal(res.data?.total || 0);
      setPage(res.data?.page || pageNumber);
    } catch (error) {
      console.error("Blogs fetch error:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to fetch blogs"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs(1, filters.search, filters.category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.category]);

  const categories = useMemo(() => {
    return [...new Set(blogs.map((blog) => blog.category).filter(Boolean))];
  }, [blogs]);

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setFilters((prev) => ({
      ...prev,
      category: value,
    }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const updatedSearch = searchInput.trim();

    setFilters((prev) => ({
      ...prev,
      search: updatedSearch,
    }));

    fetchBlogs(1, updatedSearch, filters.category);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    fetchBlogs(nextPage, filters.search, filters.category);
  };

  return (
    <section className="blogs-page">
      <div className="blogs-page-hero-wrap">
        <div className="blogs-page-hero-bg" aria-hidden="true"></div>
        <div className="blogs-page-hero-grid" aria-hidden="true"></div>

        <div className="blogs-page-hero-inner">
          <div className="blogs-page-eyebrow">
            <span className="live-dot" aria-hidden="true"></span>
            Insights &amp; Articles
          </div>

          <h1>Read our latest blogs and technical updates</h1>
          <p className="blogs-page-subtext">
            Browse engineering articles, cloud notes, infrastructure
            updates, security insights, and practical product thinking
            from our team.
          </p>
        </div>
      </div>

      <div className="blogs-toolbar">
        <form className="blogs-search-form" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search blogs..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>

        <div className="blogs-filter-group">
          <select
            name="category"
            value={filters.category}
            onChange={handleCategoryChange}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option value={category} key={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="blogs-page-container">
        {loading ? (
          <div className="blogs-grid">
            {Array.from({ length: limit }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : errorMessage ? (
          <div className="blogs-state error">
            <span className="state-mark" aria-hidden="true">
              [ ! ]
            </span>
            <p>{errorMessage}</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="blogs-state empty">
            <span className="state-mark" aria-hidden="true">
              [ ]
            </span>
            <p>No blogs have been published yet.</p>
          </div>
        ) : (
          <>
            <div className="blogs-grid">
              {blogs.map((blog) => (
                <article className="blog-list-card" key={blog.id}>
                  <Link
                    to={`/blogs/${blog.slug}`}
                    className="blog-list-frame"
                    aria-label={blog.title}
                  >
                    {blog.coverImage ? (
                      <img
                        src={blog.coverImage}
                        alt={blog.title}
                        className="blog-list-image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="blog-list-image placeholder">
                        No image
                      </div>
                    )}

                    <span className="blog-list-tag">
                      {blog.category || "Insight"}
                    </span>

                    {blog.featured ? (
                      <span className="blog-list-featured">Featured</span>
                    ) : null}

                    <span className="scan-corner tl" aria-hidden="true" />
                    <span className="scan-corner tr" aria-hidden="true" />
                    <span className="scan-corner bl" aria-hidden="true" />
                    <span className="scan-corner br" aria-hidden="true" />
                  </Link>

                  <div className="blog-list-body">
                    <div className="blog-list-meta">
                      <span>{blog.authorName || "Admin"}</span>
                      <span className="meta-dot" aria-hidden="true">
                        &middot;
                      </span>
                      <span>
                        {formatDate(blog.publishedAt || blog.createdAt)}
                      </span>
                    </div>

                    <h2 className="blog-list-title">
                      <Link to={`/blogs/${blog.slug}`}>{blog.title}</Link>
                    </h2>

                    <p className="blog-list-excerpt">{blog.excerpt}</p>

                    <div className="blog-list-tags">
                      {blog.tags?.length ? (
                        blog.tags.slice(0, 3).map((tag) => (
                          <span key={tag}>{tag}</span>
                        ))
                      ) : (
                        <span>No tags</span>
                      )}
                    </div>

                    <div className="blog-list-divider" />

                    <div className="blog-list-footer">
                      <Link
                        to={`/blogs/${blog.slug}`}
                        className="blog-list-read-more"
                      >
                        Read article <span aria-hidden="true">&rarr;</span>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="blogs-pagination">
              <button
                type="button"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                Previous
              </button>

              <span>
                Page {page} of {totalPages}
              </span>

              <button
                type="button"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
